import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { SlackApiError, type Conversation, type SlackMessage, type SlackProfile } from '@assembly/slack'
import { useUi } from '../state/ui'
import { useWorkspace } from './workspace'
import { keys } from './keys'
import { applyReaction, findMessage, removeMessage, updateMessage, upsertMessage, type HistoryData } from './cache'

const ERROR_TEXT: Record<string, string> = {
  name_taken: 'That name is already taken.',
  invalid_name_specials: 'Names can only contain lowercase letters, numbers, hyphens and underscores.',
  invalid_name_maxlength: 'Names must be 80 characters or fewer.',
  cant_update_message: 'You can only edit your own messages.',
  cant_delete_message: 'You can’t delete this message.',
  msg_too_long: 'That message is too long.',
  not_in_channel: 'Join the channel first.',
  is_archived: 'This channel is archived.',
  restricted_action: 'Your workspace doesn’t allow that.',
  missing_scope: 'Assembly doesn’t have permission for that. Try signing in again.',
  already_reacted: 'You already reacted with that emoji.',
  cant_kick_self: 'Use “Leave channel” to remove yourself.',
  ratelimited: 'Slack is rate limiting requests. Try again in a moment.',
}

export function errorMessage(err: unknown): string {
  if (err instanceof SlackApiError) return ERROR_TEXT[err.code] ?? `Slack error: ${err.code.replace(/_/g, ' ')}`
  return err instanceof Error ? err.message : String(err)
}

let pendingId = 0

export function useActions() {
  const qc = useQueryClient()
  const { account, client } = useWorkspace()
  const a = account.id
  const me = account.userId

  return useMemo(() => {
    const ui = () => useUi.getState()
    /** Runs fn, surfacing failures as a toast. Rethrows so callers can roll back. */
    const guard = async <T>(fn: () => Promise<T>, success?: string): Promise<T> => {
      try {
        const result = await fn()
        if (success) ui().showToast(success)
        return result
      } catch (err) {
        ui().showToast(errorMessage(err), 'error')
        throw err
      }
    }
    const refreshConversations = () => qc.invalidateQueries({ queryKey: keys.conversations(a) })
    const refreshConversation = (channel: string) => {
      qc.invalidateQueries({ queryKey: keys.conversation(a, channel) })
      refreshConversations()
    }

    const actions = {
      guard,

      async sendMessage(channel: string, text: string, opts: { thread_ts?: string; broadcast?: boolean } = {}) {
        // Real timestamps have 6 decimals; the extra digits keep this unique and sortable.
        const tempTs = `${(Date.now() / 1000).toFixed(6)}${String(++pendingId % 1000).padStart(3, '0')}`
        const optimistic: SlackMessage = {
          type: 'message',
          ts: tempTs,
          user: me,
          text,
          thread_ts: opts.thread_ts,
          subtype: opts.broadcast ? 'thread_broadcast' : undefined,
          pending: true,
        }
        upsertMessage(qc, a, channel, optimistic)
        try {
          const res = await client.chat.postMessage(channel, text, { thread_ts: opts.thread_ts, reply_broadcast: opts.broadcast })
          removeMessage(qc, a, channel, optimistic.ts)
          upsertMessage(qc, a, channel, { ...res.message, pending: false })
          ui().noteMessage(channel, res.message?.ts ?? res.ts)
          return res
        } catch (err) {
          removeMessage(qc, a, channel, optimistic.ts)
          ui().showToast(errorMessage(err), 'error')
          throw err
        }
      },

      meMessage: (channel: string, text: string) => guard(() => client.chat.meMessage(channel, text)),

      async uploadFiles(channel: string, files: File[], comment: string, threadTs?: string) {
        return guard(async () => {
          for (let i = 0; i < files.length; i++) {
            await client.files.upload(files[i], files[i].name, {
              channel,
              thread_ts: threadTs,
              initial_comment: i === 0 && comment ? comment : undefined,
            })
          }
          // The share appears via realtime; refresh in case we're polling.
          setTimeout(() => qc.invalidateQueries({ queryKey: keys.history(a, channel) }), 1500)
          if (threadTs) setTimeout(() => qc.invalidateQueries({ queryKey: keys.replies(a, channel, threadTs) }), 1500)
        })
      },

      async editMessage(channel: string, ts: string, text: string) {
        const before = findMessage(qc, a, channel, ts)
        updateMessage(qc, a, channel, ts, (m) => ({ ...m, text, edited: { user: me, ts: String(Date.now() / 1000) } }))
        try {
          const res = await client.chat.update(channel, ts, text)
          updateMessage(qc, a, channel, ts, (m) => ({ ...m, ...res.message, text: res.text }))
        } catch (err) {
          if (before) updateMessage(qc, a, channel, ts, () => before)
          ui().showToast(errorMessage(err), 'error')
        }
      },

      async deleteMessage(channel: string, ts: string) {
        await guard(() => client.chat.delete(channel, ts))
        removeMessage(qc, a, channel, ts)
      },

      async toggleReaction(channel: string, ts: string, name: string) {
        const msg = findMessage(qc, a, channel, ts)
        const has = !!msg?.reactions?.find((r) => r.name === name)?.users.includes(me)
        updateMessage(qc, a, channel, ts, (m) => applyReaction(m, name, me, !has))
        try {
          if (has) await client.reactions.remove(channel, ts, name)
          else await client.reactions.add(channel, ts, name)
        } catch (err) {
          if (!(err instanceof SlackApiError && (err.code === 'already_reacted' || err.code === 'no_reaction'))) {
            updateMessage(qc, a, channel, ts, (m) => applyReaction(m, name, me, has))
            ui().showToast(errorMessage(err), 'error')
          }
        }
      },

      async togglePin(channel: string, msg: SlackMessage) {
        const pinned = !!msg.pinned_to?.includes(channel)
        await guard(() => (pinned ? client.pins.remove(channel, msg.ts) : client.pins.add(channel, msg.ts)), pinned ? 'Unpinned' : 'Pinned to channel')
        updateMessage(qc, a, channel, msg.ts, (m) => ({
          ...m,
          pinned_to: pinned ? (m.pinned_to ?? []).filter((c) => c !== channel) : [...(m.pinned_to ?? []), channel],
        }))
        qc.invalidateQueries({ queryKey: keys.pins(a, channel) })
      },

      async permalink(channel: string, ts: string) {
        return guard(() => client.chat.getPermalink(channel, ts))
      },

      async copyLink(channel: string, ts: string) {
        const link = await actions.permalink(channel, ts)
        await navigator.clipboard?.writeText(link)
        ui().showToast('Link copied')
      },

      markRead: (channel: string, ts: string) => client.conversations.mark(channel, ts).catch(() => undefined),

      /** Marks a whole conversation read in Slack (not just locally), from the sidebar. */
      async markConversationRead(channel: string) {
        ui().clearUnread(channel)
        const cached = qc.getQueryData<HistoryData>(keys.history(a, channel))?.pages[0]?.messages[0]?.ts
        const latest = cached ?? ui().latest[channel] ?? (await client.conversations.history(channel, { limit: 1 }).catch(() => null))?.messages[0]?.ts
        if (latest) await client.conversations.mark(channel, latest).catch(() => undefined)
      },

      async markUnread(channel: string, ts: string) {
        // Mark everything before this message as read, so it shows as the first unread.
        const before = (Number(ts) - 0.000001).toFixed(6)
        await guard(() => client.conversations.mark(channel, before), 'Marked unread')
        ui().goHome()
        ui().bumpUnread(channel, false)
      },

      async remindAboutMessage(channel: string, ts: string, when: string) {
        const link = await actions.permalink(channel, ts)
        await guard(() => client.reminders.add(`Look at this message: ${link}`, when), 'Reminder set')
        qc.invalidateQueries({ queryKey: keys.reminders(a) })
      },

      // ---------------------------------------------------------- channels
      async createChannel(name: string, isPrivate: boolean, purpose?: string) {
        const channel = await guard(() => client.conversations.create(name, isPrivate))
        if (purpose) await client.conversations.setPurpose(channel.id, purpose).catch(() => undefined)
        qc.setQueryData<Conversation[]>(keys.conversations(a), (list) => [...(list ?? []), { ...channel, is_member: true }])
        refreshConversations()
        ui().openChannel(channel.id)
        return channel
      },

      async joinChannel(channel: string) {
        const c = await guard(() => client.conversations.join(channel))
        qc.setQueryData<Conversation[]>(keys.conversations(a), (list) => [...(list ?? []).filter((x) => x.id !== c.id), { ...c, is_member: true }])
        refreshConversation(channel)
        return c
      },

      async leaveChannel(channel: string) {
        await guard(() => client.conversations.leave(channel))
        qc.setQueryData<Conversation[]>(keys.conversations(a), (list) => list?.filter((c) => c.id !== channel))
        refreshConversation(channel)
        if (ui().channelId === channel) ui().goHome()
      },

      async closeConversation(channel: string) {
        await guard(() => client.conversations.close(channel))
        qc.setQueryData<Conversation[]>(keys.conversations(a), (list) => list?.filter((c) => c.id !== channel))
        if (ui().channelId === channel) ui().goHome()
      },

      async archiveChannel(channel: string) {
        await guard(() => client.conversations.archive(channel), 'Channel archived')
        qc.setQueryData<Conversation[]>(keys.conversations(a), (list) => list?.filter((c) => c.id !== channel))
        refreshConversation(channel)
        ui().goHome()
      },

      async unarchiveChannel(channel: string) {
        await guard(() => client.conversations.unarchive(channel), 'Channel restored')
        refreshConversation(channel)
      },

      async renameChannel(channel: string, name: string) {
        await guard(() => client.conversations.rename(channel, name))
        refreshConversation(channel)
      },

      async setTopic(channel: string, topic: string) {
        await guard(() => client.conversations.setTopic(channel, topic))
        refreshConversation(channel)
      },

      async setPurpose(channel: string, purpose: string) {
        await guard(() => client.conversations.setPurpose(channel, purpose))
        refreshConversation(channel)
      },

      async invite(channel: string, users: string[]) {
        await guard(() => client.conversations.invite(channel, users), users.length === 1 ? 'Added 1 person' : `Added ${users.length} people`)
        qc.invalidateQueries({ queryKey: keys.members(a, channel) })
        refreshConversation(channel)
      },

      async kick(channel: string, user: string) {
        await guard(() => client.conversations.kick(channel, user), 'Removed from channel')
        qc.setQueryData<string[]>(keys.members(a, channel), (list) => list?.filter((u) => u !== user))
        refreshConversation(channel)
      },

      async openDirectMessage(users: string[]) {
        const channel = await guard(() => client.conversations.open(users))
        qc.setQueryData<Conversation[]>(keys.conversations(a), (list) =>
          list?.some((c) => c.id === channel.id) ? list : [...(list ?? []), channel],
        )
        refreshConversations()
        ui().openChannel(channel.id)
        return channel
      },

      // ---------------------------------------------------------- scheduling
      async scheduleMessage(channel: string, text: string, postAt: Date, threadTs?: string) {
        await guard(
          () => client.chat.scheduleMessage(channel, text, Math.floor(postAt.getTime() / 1000), threadTs),
          `Scheduled for ${postAt.toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`,
        )
        qc.invalidateQueries({ queryKey: keys.scheduled(a) })
      },

      async deleteScheduled(channel: string, id: string) {
        await guard(() => client.chat.deleteScheduledMessage(channel, id), 'Scheduled message deleted')
        qc.invalidateQueries({ queryKey: keys.scheduled(a) })
      },

      // ---------------------------------------------------------- profile / presence
      async setProfile(profile: Partial<SlackProfile>, success = 'Profile updated') {
        const updated = await guard(() => client.users.profile.set(profile), success)
        qc.setQueryData(keys.users(a), (users: Map<string, any> | undefined) => {
          if (!users) return users
          const next = new Map(users)
          const u = next.get(me)
          if (u) next.set(me, { ...u, profile: { ...u.profile, ...updated } })
          return next
        })
      },

      setStatus: (text: string, emoji: string, expiration = 0) =>
        actions.setProfile({ status_text: text, status_emoji: emoji, status_expiration: expiration }, text || emoji ? 'Status set' : 'Status cleared'),

      async setPresence(presence: 'auto' | 'away') {
        await guard(() => client.users.setPresence(presence), presence === 'away' ? 'You’re set to away' : 'You’re set to active')
        qc.invalidateQueries({ queryKey: keys.presence(a, me) })
      },

      async snooze(minutes: number) {
        await guard(() => client.dnd.setSnooze(minutes), 'Notifications paused')
        qc.invalidateQueries({ queryKey: keys.dnd(a) })
      },

      async endSnooze() {
        await guard(() => client.dnd.endSnooze(), 'Notifications resumed')
        qc.invalidateQueries({ queryKey: keys.dnd(a) })
      },

      // ---------------------------------------------------------- reminders / bookmarks / files
      async addReminder(text: string, time: string) {
        await guard(() => client.reminders.add(text, time), 'Reminder set')
        qc.invalidateQueries({ queryKey: keys.reminders(a) })
      },
      async completeReminder(id: string) {
        await guard(() => client.reminders.complete(id))
        qc.invalidateQueries({ queryKey: keys.reminders(a) })
      },
      async deleteReminder(id: string) {
        await guard(() => client.reminders.delete(id))
        qc.invalidateQueries({ queryKey: keys.reminders(a) })
      },

      async addBookmark(channel: string, title: string, link: string, emoji?: string) {
        await guard(() => client.bookmarks.add(channel, title, link, emoji), 'Bookmark added')
        qc.invalidateQueries({ queryKey: keys.bookmarks(a, channel) })
      },
      async removeBookmark(channel: string, id: string) {
        await guard(() => client.bookmarks.remove(channel, id))
        qc.invalidateQueries({ queryKey: keys.bookmarks(a, channel) })
      },

      async deleteFile(id: string) {
        await guard(() => client.files.delete(id), 'File deleted')
        qc.invalidateQueries({ queryKey: ['files', a] })
      },
    }
    return actions
  }, [qc, a, me, client])
}

export type Actions = ReturnType<typeof useActions>
